import t, { plural } from "@repo/localization/string.macro";
import { CreditFactor as CanadaCreditFactorPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/credit/credit_factor_pb";
import { ReportAccount as CanadaReportAccountPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/credit/report_account_pb";
import { Factor as UsaFactorPb } from "@repo/protobuf/gen/web/public/credit_pb";
import Protobuf from "@repo/protobuf/utils";
import Case from "@repo/utils/Case";
import Duration, { msIn } from "@repo/utils/Duration";
import Enum from "@repo/utils/Enum";
import Interval from "@repo/utils/Interval";
import UError from "@repo/utils/UError";
import UFunction from "@repo/utils/UFunction";
import UNumber from "@repo/utils/UNumber";
import UObject from "@repo/utils/UObject";

import ReportAccount, {
  ReportAccountCanada,
  ReportAccountUsa,
} from "./ReportAccount.js";

type CreditFactor = {
  name: string;
  type: CreditFactor.Type;
  grade: CreditFactor.Grade;
  impact: CreditFactor.Impact;
  message: string;
  factorDiff: number;
  value: number;
  factorDiffTimeDeltaDays: number;
  details:
    | CreditFactor.CardUsage
    | CreditFactor.PaymentHistory
    | CreditFactor.CreditMix
    | CreditFactor.DerogatoryMarks
    | CreditFactor.CreditAge
    | CreditFactor.HardInquiries;
};

namespace CreditFactor {
  export type Grade = "na" | "a" | "b" | "c" | "d";

  export type Impact = "low" | "medium" | "high";
  export namespace Impact {
    export const format = (() => {
      const map = {
        low: () => t`Low`,
        medium: () => t`Medium`,
        high: () => t`High`,
      } as Record<Impact, () => string>;
      return (impact: Impact) => map[impact]();
    })();
  }

  export const types = [
    "cardUsage",
    "paymentHistory",
    "creditMix",
    "derogatoryMarks",
    "creditAge",
    "hardInquiries",
  ] as const;
  export type Type = (typeof types)[number];
  export type ByType = {
    cardUsage: CreditFactor.CardUsage;
    paymentHistory: CreditFactor.PaymentHistory;
    creditMix: CreditFactor.CreditMix;
    derogatoryMarks: CreditFactor.DerogatoryMarks;
    creditAge: CreditFactor.CreditAge;
    hardInquiries: CreditFactor.HardInquiries;
  };

  export const attrByType: Record<
    Type,
    { format(value: number): string | number; inverse: boolean }
  > = {
    cardUsage: {
      format: (value: number) => `${value}%`,
      inverse: true,
    },
    paymentHistory: {
      format: (value: number) => `${value} on-time`,
      inverse: false,
    },
    creditMix: {
      format: UFunction.identity,
      inverse: false,
    },
    derogatoryMarks: {
      format: UFunction.identity,
      inverse: true,
    },
    creditAge: {
      format: (value: number) => Duration.format(value * msIn.month, "short"),
      inverse: false,
    },
    hardInquiries: {
      format: UFunction.identity,
      inverse: true,
    },
  };

  export type CardUsage = {
    kikoffUtilization: CardUsage.Utilization;
    totalUtilization: CardUsage.Utilization;
    accounts: ReportAccount.Preview[];
  };
  export namespace CardUsage {
    export type Utilization = {
      name: string;
      balanceCents: number;
      limitCents: number;
    };
    export namespace Utilization {
      export const tier = Interval.Map.createLookup({
        "[0, 10)": { color: "var(--rating-a)", label: t`Excellent` },
        "[10, 30)": { color: "var(--rating-b)", label: t`Good` },
        "[30, 50)": { color: "var(--rating-c)", label: t`Fair` },
        "[50, 75)": { color: "var(--rating-d)", label: t`Poor` },
        "[75": { color: "var(--error)", label: t`Very Poor` },
      });
    }

    export const utilizationPercentage = (cardUsage: CardUsage) =>
      cardUsage.totalUtilization.limitCents > 0
        ? Math.round(
            (cardUsage.totalUtilization.balanceCents /
              cardUsage.totalUtilization.limitCents) *
              100
          )
        : 0;
  }

  export type PaymentHistory = {
    accounts: ReportAccount.Preview[];
  };

  export type CreditMix = {
    accounts: ReportAccount.Preview[];
  };

  export type DerogatoryMarks = {
    collectionsCount: number;
    publicRecordsCount: number;
    accounts: ReportAccount.Preview[];
    collections: DerogatoryMarks.Collection[];
    judgements: DerogatoryMarks.Judgements[];
  };

  export namespace DerogatoryMarks {
    export type Collection = {
      creditor: string;
      currentBalanceCents: number;
    };

    export type Judgements = {
      plaintiff: string;
      amountCents: number;
    };
  }

  export type CreditAge = {
    oldestAgeMonths: number;
    averageAgeMonths: number;
    accounts: ReportAccount.Preview[];
  };

  export type HardInquiries = {
    hardInquiriesCount: number;
    inquiries: HardInquiries.Inquiry[];
  };

  export namespace HardInquiries {
    export type Inquiry = {
      contactName: string;
      date: Date;
    };
  }
}

export default CreditFactor;

export namespace CreditFactorUsa {
  export const normalize = (proto: UsaFactorPb): CreditFactor => {
    const grade = Case.fromConstant(
      Enum.keyOf(UsaFactorPb.Grade, proto.grade)
    ).toCamel();

    const impact = Case.fromConstant(
      Enum.keyOf(UsaFactorPb.Impact, proto.impact)
    ).toCamel();

    return {
      ...UObject.pick(proto, [
        "name",
        "message",
        "factorDiff",
        "value",
        "factorDiffTimeDeltaDays",
      ]),
      grade,
      impact,
      details: Details.normalize(proto.details),
      type: Details.type(proto.details),
    };
  };

  export namespace Details {
    export const normalize = (
      proto: UsaFactorPb["details"]
    ): CreditFactor["details"] => {
      if (!proto.case)
        throw new Error("Unexpected UNKNOWN credit factor type.");

      switch (proto.case) {
        case "cardUsage":
          return CardUsage.normalize(proto.value);
        case "paymentHistory":
          return PaymentHistory.normalize(proto.value);
        case "creditMix":
          return CreditMix.normalize(proto.value);
        case "derogatoryMarks":
          return DerogatoryMarks.normalize(proto.value);
        case "creditAge":
          return CreditAge.normalize(proto.value);
        case "hardInquiries":
          return HardInquiries.normalize(proto.value);
        default:
          throw new UError.Unimplemented();
      }
    };

    export const type = (proto: UsaFactorPb["details"]): CreditFactor.Type => {
      if (!proto.case) {
        throw new Error("Unexpected UNKNOWN credit factor type.");
      }
      return proto.case;
    };

    export namespace CardUsage {
      export const normalize = (
        proto: UsaFactorPb.CardUsage
      ): CreditFactor.CardUsage => {
        return {
          kikoffUtilization: Utilization.normalize(proto.kikoffUtilization),
          totalUtilization: Utilization.normalize(proto.totalUtilization),
          accounts: proto.accounts.map(ReportAccountUsa.Preview.normalize),
        };
      };

      export namespace Utilization {
        export const normalize = (
          proto?: UsaFactorPb.CardUsage.Utilization
        ): CreditFactor.CardUsage.Utilization => {
          return {
            name: proto?.name ?? "Utilization",
            balanceCents: (proto?.balance ?? 0) * 100,
            limitCents: (proto?.limit ?? 0) * 100,
          };
        };
      }
    }

    export namespace PaymentHistory {
      export const normalize = (
        proto: UsaFactorPb.PaymentHistory
      ): CreditFactor.PaymentHistory => {
        return {
          accounts: proto.accounts.map(ReportAccountUsa.Preview.normalize),
        };
      };
    }

    export namespace CreditMix {
      export const normalize = (
        proto: UsaFactorPb.CreditMix
      ): CreditFactor.CreditMix => {
        return {
          accounts: proto.accounts.map(ReportAccountUsa.Preview.normalize),
        };
      };
    }

    export namespace DerogatoryMarks {
      export const normalize = (
        proto: UsaFactorPb.DerogatoryMarks
      ): CreditFactor.DerogatoryMarks => {
        return {
          accounts: proto.accounts.map(ReportAccountUsa.Preview.normalize),
          collections: [],
          judgements: [],
          collectionsCount: proto.collectionsCount,
          publicRecordsCount: proto.publicRecordsCount,
        };
      };
    }

    export namespace CreditAge {
      export const normalize = (
        proto: UsaFactorPb.CreditAge
      ): CreditFactor.CreditAge => {
        return {
          oldestAgeMonths: proto.oldestAgeMonths ?? 0,
          averageAgeMonths: proto.averageAgeMonths ?? 0,
          accounts: proto.accounts.map(ReportAccountUsa.Preview.normalize),
        };
      };
    }

    export namespace HardInquiries {
      export const normalize = (
        proto: UsaFactorPb.HardInquiries
      ): CreditFactor.HardInquiries => {
        return {
          hardInquiriesCount: proto.hardInquiriesCount,
          inquiries: [],
        };
      };
    }
  }
}

export namespace CreditFactorCanada {
  export function normalize(proto: CanadaCreditFactorPb): CreditFactor {
    const grade = Case.fromConstant(
      Enum.keyOf(CanadaCreditFactorPb.Grade, proto.grade)
    ).toCamel();

    const impact = Case.fromConstant(
      Enum.keyOf(CanadaCreditFactorPb.Impact, proto.impact)
    ).toCamel();

    return {
      ...proto,
      grade,
      impact,
      details: Details.normalize(proto.details),
      type: Details.type(proto.details),
      message: (() => {
        if (proto.details.case !== "paymentHistory")
          // TODO
          return "";

        const { accounts } = proto.details.value;
        const kikoffAccount = accounts.find(
          ({ isKikoffAccount }) => isKikoffAccount
        );

        const onTimePaymentsPercent = (() => {
          if (!kikoffAccount) return 0;

          const count = kikoffAccount.paymentHistory.filter(
            (payment) =>
              payment.status === CanadaReportAccountPb.Payment.Status.CURRENT
          ).length;

          return UNumber.round(
            ((count || 0) / (kikoffAccount.paymentHistory?.length || 1)) * 100
          );
        })();

        return t`You've added **${plural(
          Number.parseInt(kikoffAccount?.measureValue!) || 0,
          { one: "# on-time payment", other: "# on-time payments" }
        )}** to your record with Kikoff. This represents ${onTimePaymentsPercent}% of your payments.`;
      })(),
    };
  }

  export namespace Details {
    export const normalize = (
      proto: CanadaCreditFactorPb["details"]
    ): CreditFactor["details"] => {
      if (!proto.case)
        throw new Error("Unexpected UNKNOWN credit factor type.");

      if ("inquiries" in proto.value)
        return {
          ...proto.value,
          inquiries: proto.value.inquiries.map((inquiry) => ({
            ...inquiry,
            date: Protobuf.Timestamp.toDate(inquiry.date!),
          })),
        };

      if ("accounts" in proto.value)
        return {
          ...proto.value,
          accounts: proto.value.accounts.map(
            ReportAccountCanada.Preview.normalize
          ),
        };

      return proto.value;
    };

    export const type = (
      proto: CanadaCreditFactorPb["details"]
    ): CreditFactor.Type => {
      if (proto.case == undefined) {
        throw new Error("Unexpected UNKNOWN credit factor type.");
      }
      return proto.case;
    };
  }
}
